import React from "react";
import { useTranslation } from "react-i18next";
import { RadioGroup, Radio } from "@headlessui/react";
import { TwLabel, TwInputGroup, TwSelectGroup, TwFormHelperText, TwSwitch } from "../../components/tailwind";
import { commissionType } from "../../utils/constants";
import { decimalDisplayValue, decimalOnChange } from "../../helpers";

/**
 * Reusable commission-model editor (model type + unit + flat/tiers + bonus).
 * Used for the campaign's ticket commission and (optionally) its add-on commission.
 *
 * Props:
 *   value    { commissionModelType, unitType, flatValue, isAddBonus, bonusThreshold, bonusAmount }
 *   onChange (patch) => void         merges patch into the parent value
 *   tiers    [{ from, to?, commissionPerTicket }]
 *   setTiers (tiers) => void
 *   errors   optional form errors object
 */
export default function CommissionModelBlock({ value, onChange, tiers, setTiers, errors = {} }) {
  const { t, i18n } = useTranslation();
  const unitSym = value.unitType === "percentageOnTicketPrice" ? "%" : "€";

  const addTier = () => {
    const DEFAULT_TIER_RANGE = 9;
    const lastTier = tiers[tiers.length - 1];
    if (lastTier.from === undefined || isNaN(lastTier.from) || lastTier.commissionPerTicket === undefined || isNaN(lastTier.commissionPerTicket)) return;
    let updated = [...tiers];
    if (lastTier.to === undefined || lastTier.to === "" || isNaN(lastTier.to)) {
      updated[tiers.length - 1] = { ...lastTier, to: parseFloat(lastTier.from) + DEFAULT_TIER_RANGE };
    }
    const newFrom = parseFloat(updated[tiers.length - 1].to) + 1;
    setTiers([...updated, { from: parseFloat(newFrom), to: undefined, commissionPerTicket: 0 }]);
  };

  const removeTier = (index) => {
    const next = tiers.filter((_, i) => i !== index);
    next[next.length - 1] = { ...next[next.length - 1], to: "" };
    setTiers(next);
  };

  const handleTierChange = (index, field, val) => {
    const next = [...tiers];
    next[index] = { ...next[index], [field]: val };
    if (field === "from" && value.commissionModelType === "thresholdBased") {
      for (let i = 1; i < next.length; i++) {
        next[i - 1] = { ...next[i - 1], to: parseFloat(next[i].from) - 1 };
      }
    } else {
      next[next.length - 1] = { ...next[next.length - 1], to: undefined };
    }
    setTiers(next);
  };

  const onModelChange = (modelType) => {
    onChange({ commissionModelType: modelType });
    let seed = [];
    if (modelType === "flat") seed = [{ from: 1, commissionPerTicket: 1 }];
    else if (modelType === "thresholdBased") seed = [{ from: 1, to: 9, commissionPerTicket: 0 }, { from: 10, to: "", commissionPerTicket: 0 }];
    else seed = [{ from: 1, to: 10, commissionPerTicket: 0 }, { from: 11, to: "", commissionPerTicket: 0 }];
    setTiers([...seed]);
  };

  return (
    <div className="flex flex-col gap-3">
      <fieldset aria-label="Commission model type">
        <TwLabel>{t("myEvents.commissionModelType")}</TwLabel>
        <RadioGroup
          value={value.commissionModelType}
          onChange={onModelChange}
          className="grid grid-cols-1 gap-x-1 rounded-full bg-white/5 p-1 text-center text-xs/5 font-semibold text-gray-900"
        >
          <div className="space-x-4 flex">
            {["flat", "tiered", "thresholdBased"].map((option) => (
              <Radio key={option} value={option} className="flex items-center space-x-3 cursor-pointer">
                {({ checked }) => (
                  <>
                    <span className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${checked ? "border-blue-600" : "border-gray-300"}`}>
                      {checked && <span className="h-2.5 w-2.5 bg-blue-600 rounded-full" />}
                    </span>
                    <span className="text-gray-900 font-medium capitalize">{t("myEvents." + option)}</span>
                  </>
                )}
              </Radio>
            ))}
          </div>
        </RadioGroup>
      </fieldset>

      <TwSelectGroup
        label={t("myEvents.commissionUnitType")}
        placeholder={t("myEvents.commissionUnitType")}
        name="unitType"
        selectClass="!py-3 md:!py-3"
        options={Object.keys(commissionType).map((d) => ({ value: d, title: commissionType[d] }))}
        defaultValue={value.unitType}
        value={value.unitType}
        onChange={(e) => onChange({ unitType: e.target.value })}
        errors={errors}
      />

      {value.commissionModelType === "flat" ? (
        <TwInputGroup
          label={`${t("myEvents.entervalue")} ${unitSym}`}
          placeholder={`${t("myEvents.entervalue")} ${unitSym}`}
          type="text"
          inputMode="decimal"
          name="flatValue"
          value={decimalDisplayValue(value.flatValue || 0, i18n.language)}
          onChange={(e) => {
            const parsed = decimalOnChange(e.target.value, i18n.language);
            if (parsed !== null) {
              setTiers([{ from: 1, commissionPerTicket: parsed }]);
              onChange({ flatValue: parsed });
            }
          }}
          errors={errors}
          className="!py-3 md:pl-2.5"
        />
      ) : (
        <div className="flex flex-col gap-3">
          <TwLabel>{value.commissionModelType === "thresholdBased" ? t("myEvents.thresholds") : t("myEvents.tiers")}</TwLabel>
          {tiers.map((tier, index) => (
            <div key={index} className="flex gap-2 items-center">
              <div className="w-full flex flex-col gap-0.5">
                <TwLabel className="text-xs">{value.commissionModelType === "thresholdBased" ? t("myEvents.ticketsSold") : t("myEvents.from")}</TwLabel>
                <input
                  type="number"
                  placeholder={t("myEvents.min")}
                  disabled={value.commissionModelType === "thresholdBased" && index === 0}
                  className="mt-1 w-full rounded-2xl border border-gray-300 p-2 focus:ring-black focus:border-black"
                  value={tier.from}
                  onChange={(e) => handleTierChange(index, "from", e.target.value)}
                />
              </div>
              <div className={value.commissionModelType === "thresholdBased" ? "hidden" : "w-full flex flex-col gap-0.5"}>
                <TwLabel className="text-xs">{t("myEvents.to")}</TwLabel>
                <input
                  type="number"
                  placeholder={t("myEvents.max")}
                  className="mt-1 w-full rounded-2xl border border-gray-300 p-2 focus:ring-black focus:border-black"
                  disabled={value.commissionModelType === "thresholdBased" || tiers.length === index + 1}
                  value={tier.to}
                  onChange={(e) => handleTierChange(index, "to", e.target.value)}
                />
              </div>
              <div className="w-full flex flex-col gap-0.5">
                <TwLabel className="text-xs">{`${t("myEvents.rate")} ${unitSym}`}</TwLabel>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder={`${t("myEvents.rate")} ${unitSym}`}
                  className="mt-1 w-full rounded-2xl border border-gray-300 p-2 focus:ring-black focus:border-black"
                  value={decimalDisplayValue(tier.commissionPerTicket, i18n.language)}
                  onChange={(e) => {
                    const parsed = decimalOnChange(e.target.value, i18n.language);
                    if (parsed !== null) handleTierChange(index, "commissionPerTicket", parsed);
                  }}
                />
              </div>
              <div className="flex flex-col gap-0.5">
                <TwLabel className="text-xs">&nbsp;</TwLabel>
                <button onClick={() => removeTier(index)} className={tiers.length > 2 ? "" : "hidden"}>✕</button>
              </div>
            </div>
          ))}
          <button type="button" onClick={addTier} className="text-sm text-blue-600 hover:underline">
            {value.commissionModelType === "thresholdBased" ? t("myEvents.addThreshold") : t("myEvents.addTier")}
          </button>
        </div>
      )}

      <TwSwitch
        title={t("myEvents.addBonus")}
        name="isAddBonus"
        checked={value.isAddBonus}
        onChange={() => onChange({ isAddBonus: !value.isAddBonus, ...(value.isAddBonus ? { bonusThreshold: 0, bonusAmount: 0 } : {}) })}
      />
      {value.isAddBonus && (
        <>
          <TwInputGroup
            label={t("myEvents.bonusThreshold")}
            placeholder={t("myEvents.bonusThreshold")}
            type="number"
            name="bonusThreshold"
            value={value.bonusThreshold}
            onChange={(e) => onChange({ bonusThreshold: e.target.value })}
            errors={errors}
            className="!py-3 md:pl-2.5"
          />
          <TwInputGroup
            label={t("myEvents.bonusAmount")}
            placeholder={t("myEvents.bonusAmount")}
            type="text"
            inputMode="decimal"
            name="bonusAmount"
            value={decimalDisplayValue(value.bonusAmount, i18n.language)}
            onChange={(e) => {
              const parsed = decimalOnChange(e.target.value, i18n.language);
              if (parsed !== null) onChange({ bonusAmount: parsed });
            }}
            errors={errors}
            className="!py-3 md:pl-2.5"
          />
        </>
      )}
    </div>
  );
}
